import React, { useState } from 'react'
import Chroniqueur from './Chroniqueur'
import AudienceValidatedSummary from './AudienceValidatedSummary'
import {
  ChroniqueurResponseInterface,
  InfluencerData,
  InfluencerTableFilters,
} from '../../../core/interface/chronique.interface'
import RecapChroniqueur from '../shared/RecapChroniqueur'
import { TableViewTypeInfluencerEnum } from '../../../core/enums/table-view-type-influencer.enum'
import { InfluencerFilterYesNoEnum } from '../../../core/enums/influencer-filter.enum'
import ChroniquerProgressBar from '../shared/ChroniquerProgressBar'
import { InfluencerForInfluencedBookStatusEnum } from '../../../core/enums/influencer-status.enum'
import { InfluencedFileOutputsStatusEnum } from '../../../core/enums/influenced-file-outputs-status.enum'

interface ChroniqueurViewSwitchProps {
  reportId: string
  chronique: ChroniqueurResponseInterface
  setChronique: (chronique: ChroniqueurResponseInterface) => void
}

const ChroniqueurViewSwitch: React.FC<ChroniqueurViewSwitchProps> = ({
  reportId,
  chronique,
  setChronique,
}) => {
  const [audienceValidated, setAudienceValidated] = useState<boolean>(false)
  const [selectedUsers, setSelectedUsers] = useState<InfluencerData[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [headerFilters, setHeaderFilters] = useState<InfluencerTableFilters>({
    selection: [],
    status: [],
    alreadyInfluenced: [],
    chronique: [],
    complete: [],
    promise: [],
  })
  const [influencerFileImported, setInfluencerFileImported] =
    useState<boolean>(false)
  const [chroniqueFileImported, setChroniqueFileImported] =
    useState<boolean>(false)
  const [wordCloudGenerated, setWordCloudGenerated] = useState<boolean>(false)

  const chroniqueurId = chronique ? chronique.id_ean.toString() : ''
  const handleResetFiltersAndSelections = () => {
    setSelectedUsers([])
    setShowFilters(false)
    setHeaderFilters({
      selection: [],
      status: [],
      alreadyInfluenced: [],
      chronique: [],
      complete: [],
      promise: [],
    })
  }

  const handleValidate = (selectedUsers: InfluencerData[]) => {
    setSelectedUsers(selectedUsers)
    setAudienceValidated(true)
  }

  const customTitle = `${chronique.title} - ${chronique.authors.join(', ')} (${
    chronique.edition
  }) - ${chronique.publication_date}`

  const isInfluencerSelected = chronique.influencers.some(
    (influencer) =>
      influencer.current_influencedbook_status ===
      InfluencerForInfluencedBookStatusEnum.SELECTED,
  )
  const chroniqueImportedFiles = chronique.influenced_file_outputs.filter(
    (output) => output.status === InfluencedFileOutputsStatusEnum.CHRONIQUES,
  )
  const promisesImportedFiles = chronique.influenced_file_outputs.filter(
    (output) => output.status === InfluencedFileOutputsStatusEnum.SIGNUP,
  )

  let currentStep: number | null = 3
  const completedSteps = [1, 2]
  if (influencerFileImported || chroniqueImportedFiles.length > 0) {
    currentStep = 4
    completedSteps.push(3)
  }
  if (isInfluencerSelected) {
    currentStep = 5
    completedSteps.push(4)
  }
  if (chroniqueFileImported || promisesImportedFiles.length > 0) {
    currentStep = 6
    completedSteps.push(5)
  }
  if (chronique.word_cloud || wordCloudGenerated) {
    currentStep = null
    completedSteps.push(6)
  }
  return (
    <div className="flex-auto flex flex-col px-8 py-4 gap-4 w-full">
      <div
        className="flex-auto flex flex-col bg-white rounded-xl px-8 pt-8 pb-4 h-[80vh] relative"
        style={{
          boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
        }}
      >
        <RecapChroniqueur
          viewType={TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW}
          selectedUsersCount={selectedUsers.length}
          bookSelected={customTitle}
          setShowFilters={setShowFilters}
          showFilters={showFilters}
          handleResetFiltersAndSelections={
            audienceValidated ? () => {} : handleResetFiltersAndSelections
          }
        />

        <ChroniquerProgressBar
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
        {chronique &&
          (audienceValidated ? (
            <AudienceValidatedSummary
              reportId={reportId}
              chroniqueId={chroniqueurId}
              selectedUsers={selectedUsers}
            />
          ) : (
            <Chroniqueur
              chronique={chronique}
              setChronique={setChronique}
              showFilters={showFilters}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              headerFilters={headerFilters}
              setHeaderFilters={setHeaderFilters}
              onValidate={handleValidate}
              setInfluencerFileImported={setInfluencerFileImported}
              setChroniqueFileImported={setChroniqueFileImported}
              setWordCloudGenerated={setWordCloudGenerated}
              chroniqueImportedFiles={chroniqueImportedFiles}
              promisesImportedFiles={promisesImportedFiles}
            />
          ))}
      </div>
    </div>
  )
}

export default ChroniqueurViewSwitch
